import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUsers, FiHeart, FiGlobe, FiShield, FiTrendingUp, FiTarget, FiCheck, FiAward } = FiIcons;

const AboutPage = () => {
  const partners = [
    {
      name: 'Maysalward',
      role: 'Technology Partner',
      description: 'Leading the technical development and platform innovation',
      logo: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=200'
    },
    {
      name: 'Youth, Technology and Jobs (YTJ)',
      role: 'Implementation Partner',
      description: 'Coordinating youth engagement and job creation initiatives',
      logo: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=200'
    },
    {
      name: 'Ministry of Digital Economy and Entrepreneurship (MoDEE)',
      role: 'Government Partner',
      description: 'Providing policy support and strategic oversight',
      logo: 'https://images.unsplash.com/photo-1541746972996-4e0b0f93e586?w=200'
    }
  ];

  const features = [
    {
      icon: FiUsers,
      title: 'Verified Teacher Profiles',
      description: 'Browse authenticated educators with documented needs and transparent funding requirements'
    },
    {
      icon: FiShield,
      title: 'Secure Donations',
      description: 'Safe and transparent contribution process with local payment gateway integration'
    },
    {
      icon: FiTrendingUp,
      title: 'Progress Tracking',
      description: 'Real-time funding progress with detailed impact stories and automated receipts'
    },
    {
      icon: FiGlobe,
      title: 'Multilingual Support',
      description: 'Mobile-first design with full Arabic and English language support'
    },
    {
      icon: FiTarget,
      title: 'Targeted Support',
      description: 'Specific funding for devices, learning materials, internet access, and training'
    },
    {
      icon: FiAward,
      title: 'Impact Measurement',
      description: 'Comprehensive analytics and reporting for partners and stakeholders'
    }
  ];

  const stats = [
    { label: 'Teachers Supported', value: '2,847', icon: FiUsers },
    { label: 'Projects Funded', value: '15,234', icon: FiHeart },
    { label: 'Students Impacted', value: '89,567', icon: FiTrendingUp },
    { label: 'Success Rate', value: '94%', icon: FiCheck }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-600 to-primary-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              About Our Mission
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-primary-100 max-w-4xl mx-auto">
              Transforming education through technology, collaboration, and community support
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Story Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
              Our Story
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
              <p className="mb-6 text-xl">
                <strong>Maysalward</strong>, in collaboration with <strong>Youth, Technology and Jobs (YTJ)</strong> and the <strong>Ministry of Digital Economy and Entrepreneurship (MoDEE)</strong>, launched a teacher donation website that streamlines support for educators and classrooms.
              </p>
              
              <p className="mb-6">
                The platform allows donors to browse verified teacher profiles, see specific needs (devices, learning materials, internet access, and training), and contribute securely with full transparency and progress tracking. Teachers submit documented requests that are reviewed and approved to ensure equity and impact.
              </p>
              
              <p className="mb-6">
                The site highlights impact stories, shows funding progress, and issues automated receipts. Built mobile-first with Arabic and English support, it integrates local payment gateways and provides partner dashboards for real-time monitoring, reporting, and program analytics.
              </p>
              
              <p>
                This collaborative initiative represents a significant step forward in digital transformation of educational support, ensuring that every teacher has access to the resources they need to provide quality education.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Partners
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A powerful collaboration between technology, youth development, and government support
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {partners.map((partner, index) => (
              <motion.div
                key={partner.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-xl shadow-lg p-8 text-center"
              >
                <img
                  src={partner.logo}
                  alt={partner.name}
                  className="w-20 h-20 rounded-full mx-auto mb-6 object-cover"
                />
                <h3 className="text-xl font-bold text-gray-900 mb-2">{partner.name}</h3>
                <p className="text-primary-600 font-semibold mb-4">{partner.role}</p>
                <p className="text-gray-600">{partner.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Platform Features
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Advanced technology solutions designed for transparency, security, and impact
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                  <SafeIcon icon={feature.icon} className="text-2xl text-primary-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="py-16 bg-primary-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Our Impact
            </h2>
            <p className="text-xl text-primary-100 max-w-3xl mx-auto">
              Real results from our collaborative effort to support education
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-primary-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={stat.icon} className="text-2xl text-white" />
                </div>
                <div className="text-3xl font-bold mb-2">{stat.value}</div>
                <div className="text-primary-100">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Our Vision for the Future
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                We envision a world where every teacher has access to the resources they need, 
                where technology bridges the gap between need and support, and where communities 
                come together to invest in education.
              </p>
              <p className="text-lg text-gray-700 mb-8">
                Through innovative partnerships and cutting-edge technology, we're building 
                a sustainable ecosystem that empowers educators and transforms lives.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center space-x-2 text-primary-600">
                  <SafeIcon icon={FiCheck} />
                  <span className="font-semibold">Transparency</span>
                </div>
                <div className="flex items-center space-x-2 text-primary-600">
                  <SafeIcon icon={FiCheck} />
                  <span className="font-semibold">Innovation</span>
                </div>
                <div className="flex items-center space-x-2 text-primary-600">
                  <SafeIcon icon={FiCheck} />
                  <span className="font-semibold">Impact</span>
                </div>
                <div className="flex items-center space-x-2 text-primary-600">
                  <SafeIcon icon={FiCheck} />
                  <span className="font-semibold">Collaboration</span>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600"
                alt="Students learning"
                className="rounded-xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-secondary-500 text-white p-6 rounded-xl shadow-lg">
                <div className="text-2xl font-bold">100%</div>
                <div className="text-sm">Transparency</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;